import { Modal } from "reactstrap";
import styles from './MultiFileUploadModal.module.scss';
import { Field, useForm } from "react-final-form";
import { getIn } from "final-form";
import MeritoButton from "../../../../components/common/MeritoButtons/MeritoButtons";
import { MdOutlineFileUpload as UploadIcon } from 'react-icons/md'
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { pdfProtectionStates, UnderwritingSteps, uploadStates } from "../../../../interfaces/interfaces";
import { UnderwritingFormContext } from "../../context/UnderwritingFormContext";
import { v1 as uuidv1 } from "uuid";
import { FieldArray } from "react-final-form-arrays";
import { RiCloseFill as RemoveIcon } from 'react-icons/ri'
import { VscFilePdf as PdfIcon } from 'react-icons/vsc';
import CircularLoader from "../../../../components/common/CircularLoader/CircularLoader";
import { addDelay, checkPDFProtection } from "../utils/functions";
import FileItem from "./FileItem";
import API from "../../../../rest/api";

type Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    fieldName: string;
    title: React.ReactNode;
    subtitle: React.ReactNode;
    stepType: UnderwritingSteps;
    uploadSectionType: string;
    newUploadedDocIds: number[];
    setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
    deleteDocIds: number[],
    setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
}

const MultiFileUploadModal = ({ isOpen, setIsOpen, fieldName, title, subtitle, stepType, uploadSectionType, newUploadedDocIds, setNewUploadedDocIds, deleteDocIds, setDeleteDocIds }: Props) => {
    const { getState, change } = useForm();

    const { handleUnderwritingFileJobs, underwritingFileJobs, sendFileJobToApi } = useContext(UnderwritingFormContext);

    const allFileState = getIn(getState()?.values, fieldName);
    const fileInputRef = useRef(null);
    const [invalidFileError, setInvalidFileError] = useState(null);

    const validateFile = (file: File | undefined) => {
        // include excel file type
        const allowedFormats = [
            'application/pdf',
            'image/jpeg',
            'image/png',
            'application/vnd.ms-excel', // .xls
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            ''
        ];
        const fileSizeLimit = 30; // in MB
        const maxFileSize = fileSizeLimit * 1024 * 1024; // Convert MB to bytes

        if (!file) {
            return 'Upload document';
        }

        if (!allowedFormats.includes(file.type)) {
            return 'Only PDF, JPG, PNG, and Excel files are accepted';
        }

        if (file.size > maxFileSize) {
            return `File size should be less than ${fileSizeLimit} MB`;
        }

        return null;
    }

    const uploadFileToS3 = async (fieldName: string, fileData: File, fileJob: any, fileFormJob: any,) => {
        try {
            // await addDelay(5000);
            const formData = new FormData();
            formData.append('documentType', uploadSectionType);
            formData.append('file', fileData);
            const data = await API.post('/app/uploadDocs', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }).then((res) => res.data);
            const isPdfProtected = await checkPDFProtection(fileData);
            let jobDetails = null
            if (fileData.type == 'application/pdf') {
                if (isPdfProtected == pdfProtectionStates.passwordNotRequired) {
                    // password not required
                    jobDetails = await sendFileJobToApi(stepType, fieldName, {
                        s3FileKey: data?.Key,
                        fileMetaData: {
                            fileName: fileData?.name,
                            fileType: fileData?.type,
                            requiredPassword: false,
                            password: null,
                        },
                        status: uploadStates.doneUploading,
                    })
                } else if (isPdfProtected == pdfProtectionStates.passwordRequired) {
                    // password required
                    jobDetails = await sendFileJobToApi(stepType, fieldName, {
                        s3FileKey: data?.Key,
                        fileMetaData: {
                            fileName: fileData?.name,
                            fileType: fileData?.type,
                            requiredPassword: true,
                            password: null,
                        },
                        status: uploadStates.requiredPassword,
                    })
                }
            } else {
                jobDetails = await sendFileJobToApi(stepType, fieldName, {
                    s3FileKey: data?.Key,
                    fileMetaData: {
                        fileName: fileData?.name,
                        fileType: fileData?.type,
                        requiredPassword: false,
                        password: null,
                    },
                    status: uploadStates.doneUploading,
                });
            }
            setNewUploadedDocIds(prev => [...prev, jobDetails?.docId]);
            change(`${fieldName}`, {
                ...fileFormJob,
                uploadedFile: {
                    fileKey: data?.Key,
                    fileName: fileData?.name,
                    fileType: fileData?.type,
                    docId: jobDetails?.id,
                }
            });
        } catch (error) {
            console.log('error', error);
        }
    }

    const handleFileChange = (event) => {
        console.log('event', event.target.files);
        const newFiles: File[] = Array.from(event.target.files);

        let fileError = null;
        newFiles.forEach((file) => {
            const error = validateFile(file);
            if (error) {
                // errorFiles.push(file?.name)
                fileError = error;
            }
        });
        if (fileError) {
            setInvalidFileError({
                title: "The Uploaded files(s) is invalid",
                subTitle: fileError,
            });
            return;
        } else {
            setInvalidFileError(null);
        }
        console.log('new Files', newFiles);
        const existingFiles = getIn(getState().values, fieldName) || [];
        const updatedFiles = [...existingFiles];
        newFiles.forEach((file, index) => {

            const fileFormObj = {
                selectedFile: file,
                uploadedFile: null,
            }
            updatedFiles.push(fileFormObj);
            const currentFieldName = `${fieldName}[${updatedFiles.length - 1}]`;
            const fileJob = {
                jobId: uuidv1(),
                step: stepType,
                section: uploadSectionType,
                formFieldName: currentFieldName,
                s3FileKey: null,
                fileMetaData: {
                    fileName: file.name,
                    fileType: file.type,
                    requiredPassword: false,
                    password: null,
                },
                status: uploadStates.uploading,
            };
            change(currentFieldName, fileFormObj)

            handleUnderwritingFileJobs(stepType, currentFieldName, fileJob);
            uploadFileToS3(currentFieldName, file, fileJob, fileFormObj);
        });
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={() => {
                setIsOpen(false);
            }}
            size="lg"
            backdrop={"static"}
            keyboard={false}
            centered={true}
        >
            <div className={styles.multiFileUploadRootContainer}>
                <div className={styles.headerContainer}>
                    <div className={styles.title}>
                        {title}
                    </div>
                    <div className={styles.subtitle}>
                        {subtitle}
                    </div>
                </div>

                <div className={styles.centerContainer}>
                    <div className={`${styles.uploadRootContainer} ${!allFileState?.length ? styles.coldState : styles.filesUploaded}`}>
                        <div className={`${styles.uploadContainer}`}>
                            <div className={styles.uploadBtnContainer}>
                                <div className={styles.btnContainer}>
                                    <MeritoButton
                                        btnText="Upload Files"
                                        type="secondary-2"
                                        size="lg"
                                        isRounded={true}
                                        icon={<UploadIcon />}
                                        onClick={() => {
                                            fileInputRef.current.click();
                                        }}
                                    />
                                    <input
                                        type="file"
                                        multiple
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        style={{ display: "none" }}
                                        accept='.pdf,.jpg,.jpeg,.png,.xls,.xlsx'
                                    />
                                </div>
                            </div>
                            <div className={styles.divider}></div>
                            <div className={styles.subText}>
                                You can upload multiple files.
                            </div>
                        </div>
                    </div>
                    <FieldArray name={fieldName}>
                        {({ fields }) => (
                            <>
                                {fields.length ? <div className={styles.uploadedFilesRootContainer}>
                                    <div className={styles.headerTitle}>
                                        {`Uploaded Files (${fields.length})`}
                                    </div>
                                    <div className={styles.uploadedFileListContainer}>
                                        {fields.map((innerFieldName, index) => {
                                            return (
                                                <FileItem
                                                    outerFieldName={fieldName}
                                                    fieldName={innerFieldName}
                                                    stepType={stepType}
                                                    newUploadedDocIds={newUploadedDocIds}
                                                    setNewUploadedDocIds={setNewUploadedDocIds}
                                                    deleteDocIds={deleteDocIds}
                                                    setDeleteDocIds={setDeleteDocIds}
                                                    fieldIndex={index}
                                                />
                                            )
                                        })}
                                    </div>
                                </div> : null}
                            </>
                        )}
                    </FieldArray>
                </div>

                <div className={styles.bottomContainer}>
                    <MeritoButton
                        type="secondary-2"
                        btnText={"Close"}
                        isRounded={true}
                        onClick={() => {
                            setIsOpen(false);
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default MultiFileUploadModal;